import { AuthenticatorConfiguration, AuthenticatorType, useRepositoryMutation } from '@mews-commander/data-access';
import { LocalizedText, showErrorAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button } from '@optimus-web/core';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { authenticateWithSecondFactor } from '../../../ducks/actions';
import { Configure2FALayout } from '../Configure2FALayout';
import { TwoFactorForm, TwoFactorFormValues } from '../../../components/TwoFactorForm';

export interface Configure2FADeviceFormProps {
    authenticator?: AuthenticatorConfiguration;
    onCancel: () => void;
    onSuccess: (recoveryPins: string[]) => void;
}

export function Configure2FADeviceForm({ onSuccess, authenticator, onCancel }: Configure2FADeviceFormProps) {
    const { onSubmit } = use2FASubmitHandler({ secretKey: authenticator?.secretKey, onSuccess });

    return (
        <Configure2FALayout title={TextKey.VerifyIdentity} subtitle={TextKey.VerifyIdentityInstructions}>
            <TwoFactorForm
                onSubmit={onSubmit}
                id="pin-email-form-settings"
                pinAutofocus
                withPassword
                actions={{
                    primary: (
                        <Button type="submit" size="medium" variant="primary">
                            <LocalizedText textKey={TextKey.Activate} />
                        </Button>
                    ),
                    secondary: (
                        <Button type="button" size="medium" onClick={onCancel}>
                            <LocalizedText textKey={TextKey.GoBack} />
                        </Button>
                    ),
                }}
            />
        </Configure2FALayout>
    );
}

function use2FASubmitHandler({
    secretKey,
    onSuccess,
}: Pick<AuthenticatorConfiguration, 'secretKey'> & { onSuccess: (recoveryPins: string[]) => void }) {
    const dispatch = useCommanderThunkDispatch();

    const { mutateAsync } = useRepositoryMutation('users', 'updateAuthenticator', {
        meta: {
            disableDefaultErrorHandler: true,
        },
    });

    const onSubmit = async ({ pin, password = '' }: TwoFactorFormValues) => {
        // We remove spaces from the pin to ensure a better UX
        const cleanPin = pin.replace(/\s/g, '');
        try {
            const { recoveryPins } = await mutateAsync({
                authenticator: AuthenticatorType.Google,
                pin: cleanPin,
                password,
                secretKey,
            });
            await dispatch(authenticateWithSecondFactor({ pin: cleanPin, redirectToDashboard: false }));
            onSuccess(recoveryPins ?? []);
        } catch (error) {
            showErrorAlert({ description: <>{error.message}</> });
        }
    };

    return { onSubmit };
}
